<template>
  <div class="pa-16 pt-7">
    <v-card class="elevation-0 d-flex">
      <div>
        <v-img class="mb-6 mx-auto" src="@/assets/img/geotarget.png" width="150px"></v-img>
      </div>
      <div>
        <v-card-text class="text-h5 black--text">Осталось только назначить встречу</v-card-text>
        <v-card-text class="text-body-1 black--text"
          >Можем встретиться в офисе банка, если он есть в вашем городе. Или наш менеджер сам приедет в удобное вам
          место.</v-card-text
        >
      </div>
    </v-card>

    <v-divider></v-divider>

    <v-row>
      <v-col cols="8">
        <v-form ref="form" v-model="valid">
          <div class="text-h6 my-2">Выберите место встречи</div>
          <v-btn-toggle v-model="place" color="primary" class="mb-7">
            <v-btn
              value="office"
              outlined
              :disabled="!isCitiesAvailable"
              class="custom-transform-class text-none text-subtitle-1"
              id="office-btn"
            >
              В офисе банка
            </v-btn>

            <v-btn
              value="address"
              outlined
              class="custom-transform-class text-none text-subtitle-1"
            >
              В удобном вам месте
            </v-btn>

            <v-btn
              v-if="isVideoAvailable"
              value="video"
              outlined
              class="custom-transform-class text-none text-subtitle-1"
            >
              Видеовстреча
            </v-btn>
          </v-btn-toggle>

          <!-- Выбор местоположений в зависимости от типа встречи -->
          <template v-if="place === 'office' || place === 'video'">
            <v-select
              label="Город"
              :items="meetingCitiesWithOffices"
              item-value="city_name"
              item-text="city_name"
              clearable
              dense
              :error-messages="placeSelectError"
              :rules="[rules.required('Выберите ваш город')]"
              class="ma-0"
              outlined
              v-model="geo"
              @click:clear="clearResult"
            />

            <div v-show="place === 'office' && geo">
              <v-radio-group v-model="address" @change="changeAddress" class="ma-0 pt-0">
                <v-radio
                  v-for="office in selectedCityOffices"
                  :key="office.address"
                  :label="office.address"
                  :value="office.address"
                  color="green"
                  :rules="[rules.required('Выберите адрес офиса')]"
                ></v-radio>
              </v-radio-group>
            </div>
          </template>
          <template v-else-if="place === 'address'">
            <BidSearchAddress
              class="mb-3"
              dense
              :error-messages="placeSelectError"
              :rules="[rules.required('')]"
              :filter="filterAddress"
              @input="liveSearchAddress"
              @clear-address="clearAddress"
            />

            <div class="d-flex font font--note font--color_light mb-sm-0 mb-2">
              <div class="mr-3">
                <v-icon size="18px">{{ "mdi-information-outline" }}</v-icon>
              </div>
              <div class="mb-3">Мы проводим выездные встречи только в общественных местах</div>
            </div>
          </template>

          <!-- Выбор слота из календаря -->
          <template v-if="place && (place === 'video' && geo || address)">
            <template v-if="hasTimeSlots === true">
              <div class="text-h6 my-2">Выберите дату встречи</div>
              <v-row>
                <v-col cols="6">
                  <v-menu
                    v-model="menu"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                    :close-on-content-click="false"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        ref="date"
                        :value="formattedDate"
                        persistent-hint
                        autocomplete="off"
                        outlined
                        dense
                        prepend-inner-icon="mdi-calendar"
                        class="filter-date"
                        readonly
                        label="Дата"
                        v-bind="attrs"
                        :rules="[rules.required('Выберите дату')]"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="dateSelected"
                      :first-day-of-week="1"
                      :allowed-dates="allowedDates"
                      no-title
                      scrollable
                      light
                      locale="ru-RU"
                      @change="menu = false"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="6">
                  <v-select
                    label="Время"
                    :items="slotsInDay"
                    item-value="value"
                    item-text="text"
                    dense
                    outlined
                    v-model="timeId"
                    no-data-text="Выберите адрес и дату"
                    :rules="[rules.required('Выберите время встречи')]"
                  />
                </v-col>
              </v-row>

              <div class="d-flex">
                <v-btn class="mr-2" color="primary" :disabled="!valid" @click="makeAppointment">Назначить встречу</v-btn>
              </div>
            </template>
            <template v-else-if="hasTimeSlots === false">
              <div class="mt-3 red--text text--lighten-1">
                Выберите другой адрес.
              </div>
            </template>
          </template>
        </v-form>
      </v-col>
      <!-- <v-col cols="6">Карта</v-col> -->
    </v-row>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from "vuex";
import { formatDate } from "@/utils/helpers";
import { meetingReserve, meetingSlots } from "@/api";
import BidSearchAddress from "@/components/bids/BidSearchAddress";

export default {
  name: "BidMeetingPlace",

  components: {
    BidSearchAddress,
  },

  data() {
    return {
      valid: false,
      address: null,
      timeId: null,
      places_list: {
        'office': {
          title: "Офис",
          api_name: 'Офис'
        },
        'address': {
          title: "Выезд",
          api_name: 'Выезд'
        },
        'video': {
          title: "Видеовстреча",
          api_name: 'Видеовстреча'
        },
      },
      place: 'office',
      placeSelectError: null,
      dateSelected: null,
      geo: null,
      menu: false,
      zoneId: null,
      rules: {
        required: (msg) => (v) => !!v || msg
      }
    };
  },

  computed: {
    ...mapState("bid", ["reserve_meeting", "bidId"]),
    ...mapGetters("bid", ["slots"]),

    meetingCities() {
      return this.reserve_meeting?.cities;
    },

    meetingCitiesWithOffices() {
      if (!this.isCitiesAvailable) {
        return null;
      }

      return this.meetingCities.filter(city => Array.isArray(city.offices) ? city.offices.length !== 0 : false);
    },

    formattedDate() {
      return formatDate(this.dateSelected);
    },

    slotsInDay()
    {
      if (this.dateSelected && this.hasTimeSlots === true)
      {
        const timeSlots = this.slots[this.dateSelected];
        return timeSlots.map(e => ({
          text: new Date(e.start).toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"}),
          value: e.id,
        }));
      }

      return [];
    },

    isCitiesAvailable() {
      return this.meetingCities.length !== 0;
    },

    isVideoAvailable() {
      return (this.reserve_meeting?.disable_video ?? false) !== true;
    },

    selectedCity() {
      if (!this.isCitiesAvailable) {
        return null;
      }

      return this.meetingCities.find(geo => this.geo === geo.city_name);
    },

    selectedCityOffices() {
      return this.selectedCity?.offices;
    },

    hasTimeSlots() {
      if (this.slots === null) {
        return null;
      }

      return Object.keys(this.slots).length !== 0;
    },
  },

  watch: {
    slotsInDay(e)
    {
      if (e.length < 1) {
        this.dateSelected = null;
        this.$refs.date.resetValidation();
      }
    },

    place(newValue)
    {
      this.clearResult();

      if (newValue === 'office' && this.address) {
        this.addSlots();
      }
    },

    geo(newValue)
    {
      if (this.place === 'video' && newValue) {
        this.addSlots()
      }
    },

    hasTimeSlots(v) {
      if (v === false) {
        this.placeSelectError = "К сожалению, по выбраному адресу нет доступного времени";
      } else {
        this.placeSelectError = null;
      }
    },
  },

  methods: {
    ...mapMutations("bid", ["setSlots"]),

    allowedDates(val) {
      if (!this.slots)
        return null;

      return val in this.slots;
    },

    async addSlots() {
      const data = {
        app_id: this.bidId,
        meeting_place: this.places_list[this.place].api_name,
        address: this.address,
      };

      if (this.place === 'video') { // именно у видеовстреч один календарь, получаем от сервера нужные слоты со смещением во времени
        data.city = this.geo;
      }

      if (typeof this.zoneId === 'number') {
        data.zone_id = this.zoneId;
      }

      const res = await meetingSlots(data);
      if (res) {
        this.setSlots(Array.isArray(res) ? res : []);
      }
    },

    // Назначить встречу
    async makeAppointment() {
      let data = {
        app_id: this.bidId,
        slot_id: this.timeId,
        meeting_place: this.places_list[this.place].api_name
      };

      if (this.place === 'address' || this.place === 'office') {
        data.address = this.address;
      }

      const res = await meetingReserve(data);
      if (res) {
        this.$store.commit("bid/set", { type: "showBidDetails", value: false });
      }
    },

    async changeAddress(address)
    {
      this.setSlots(null);

      if (address) {
        this.address = address;
        await this.addSlots()
      }
    },

    filterAddress: item => {
      const zone_id = item?.zone_id;
      return typeof zone_id === 'number';
    },

    // поиск адреса для Выезда
    async liveSearchAddress(current)
    {
      if (current) {
        this.address = current.value;
        this.zoneId = current.zone_id;
        await this.addSlots();
      }
      else {
        this.value = "";
        this.zoneId = "";
      }
    },

    resetForm() {
      this.clearResult();

      this.place = 'office';
      this.timeId = null;
      this.$refs.form.reset();
      this.$refs.form.resetValidation();
    },

    clearResult() {
      this.setSlots(null);
      this.clearAddress();

      this.geo = this.timeId = this.dateSelected = null;
    },

    clearAddress(){
      this.address = null;
    }
  },
};
</script>

<style lang="scss" scoped></style>
